import { useState, useEffect } from 'react'
import LoginScreen from './components/auth/LoginScreen'
import CounselorDashboard from './components/counselor/CounselorDashboard'
import ClientView from './components/client/ClientView'
import BaselineQuestionnaire from './components/client/BaselineQuestionnaire'
import GDPRConsentModal from './components/auth/GDPRConsentModal'
import ProspectLanding from './components/public/ProspectLanding'
import PublicSomaticExperiment from './components/public/PublicSomaticExperiment'
import PublicInsightForm from './components/public/PublicInsightForm'
import { sanitizeText } from './utils/sanitize'
import { AuthService } from './services/authService'
import { DataManager } from './services/dataManager'
import { FirebaseService } from './services/firebaseService'

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showProspect, setShowProspect] = useState(false);
    const [currentPath, setCurrentPath] = useState(window.location.pathname);

    useEffect(() => {
        const handleLocationChange = () => {
            setCurrentPath(window.location.pathname);
        };
        window.addEventListener('popstate', handleLocationChange);
        return () => window.removeEventListener('popstate', handleLocationChange);
    }, []);

    const navigate = (path) => {
        window.history.pushState({}, '', path);
        window.dispatchEvent(new Event('popstate'));
    };

    useEffect(() => {
        // Handle Google Sign-In redirect result (if popup was blocked)
        AuthService.handleRedirectResult().catch(err => {
            console.error("Redirect result error:", err);
        });

        // Fallback bootstrap admin emails (for initial seed / emergency recovery)
        const BOOTSTRAP_COUNSELORS = [
            'nuvola@awarenesstool.it',
            'martina@gmail.com',
            'marilisa.delve@gmail.com',
            'marzia.macca@gmail.com',
            'trebino.martina@gmail.com',
            'patrizia.marforio@gmail.com',
            'patrizia.marchi.1969@gmail.com',
            'zgaranitassini@gmail.com'
        ];

        const unsubscribe = AuthService.observeAuth(async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const userEmail = firebaseUser.email?.toLowerCase() || '';

                    // 1. Check if COUNSELOR or ORG_ADMIN (by UID in counselors collection, or by Email, or Bootstrap list)
                    let counselorDoc = null;
                    try {
                        counselorDoc = await FirebaseService.getCounselorByUid(firebaseUser.uid);
                        if (!counselorDoc && userEmail) {
                            counselorDoc = await FirebaseService.getCounselorByEmail(userEmail);
                        }
                    } catch (cErr) {
                        console.warn("Counselor check failed:", cErr);
                    }

                    const isBootstrapCounselor = BOOTSTRAP_COUNSELORS.includes(userEmail);
                    const isCounselor = !!counselorDoc || isBootstrapCounselor;

                    if (isCounselor) {
                        let counselorName = counselorDoc?.name || firebaseUser.displayName || 'Counselor';
                        if (userEmail === 'marzia.macca@gmail.com' && (!counselorDoc?.name || counselorDoc.name === 'Counselor')) {
                            counselorName = 'Marzia M.';
                        } else if (userEmail === 'trebino.martina@gmail.com' && (!counselorDoc?.name || counselorDoc.name === 'Counselor')) {
                            counselorName = 'Martina Trebino';
                        } else if (userEmail === 'martina@gmail.com' && (!counselorDoc?.name || counselorDoc.name === 'Counselor' || counselorDoc.name.toLowerCase().includes('trebino') || counselorDoc.name.toLowerCase().includes('tavarone'))) {
                            counselorName = 'Martina Vaccarone';
                        } else if (userEmail === 'patrizia.marforio@gmail.com' && (!counselorDoc?.name || counselorDoc.name === 'Counselor')) {
                            counselorName = 'Patrizia Marforio';
                        } else if (userEmail === 'patrizia.marchi.1969@gmail.com' && (!counselorDoc?.name || counselorDoc.name === 'Counselor')) {
                            counselorName = 'Patrizia Marchi';
                        }
                        const isAdmin = counselorDoc?.role === 'admin' || counselorDoc?.role === 'superadmin' || counselorDoc?.isAdmin === true || userEmail === 'nuvola@awarenesstool.it' || userEmail === 'marilisa.delve@gmail.com';
                        const isOrgAdmin = counselorDoc?.role === 'org_admin' || userEmail === 'patrizia.marforio@gmail.com' || userEmail === 'marzia.macca@gmail.com' || userEmail === 'trebino.martina@gmail.com' || userEmail === 'patrizia.marchi.1969@gmail.com';
                        const allowedOrganizations = counselorDoc?.allowedOrganizations || (isOrgAdmin ? ['buenavista'] : null);
                        const canOrganizational = isOrgAdmin ? true : (counselorDoc?.canOrganizational ?? true);

                        // Ensure counselor doc exists with correct UID, and clean up duplicate legacy/seed docs
                        if (!counselorDoc || counselorDoc.id !== firebaseUser.uid) {
                            const oldDocId = counselorDoc?.id;
                            FirebaseService.createCounselor(
                                firebaseUser.uid,
                                userEmail,
                                counselorName,
                                isAdmin ? 'admin' : (isOrgAdmin ? 'org_admin' : (counselorDoc?.role || 'counselor')),
                                {
                                    canOrganizational: canOrganizational,
                                    allowedOrganizations: allowedOrganizations,
                                    organizationId: isOrgAdmin ? 'buenavista' : (counselorDoc?.organizationId || null),
                                    organizationName: isOrgAdmin ? 'BuenaVista Cooperativa Sociale' : (counselorDoc?.organizationName || null),
                                    specializations: isOrgAdmin ? ['individuale', 'organizzativo', 'people_care'] : (counselorDoc?.specializations || ['individuale', 'organizzativo'])
                                }
                            ).then(() => {
                                // If there was an old document with different ID (e.g. seed ID), delete old duplicate doc
                                if (oldDocId && oldDocId !== firebaseUser.uid) {
                                    FirebaseService.deleteCounselor(oldDocId).catch(delErr => console.info("Old counselor doc cleanup notice:", delErr));
                                }
                            }).catch(err => console.warn("Auto-sync counselor doc failed:", err));
                        }

                        setUser({
                            id: firebaseUser.uid,
                            name: counselorName,
                            email: firebaseUser.email,
                            role: isOrgAdmin ? 'org_admin' : (isAdmin ? 'admin' : 'counselor'),
                            isAdmin: isAdmin,
                            isOrgAdmin: isOrgAdmin,
                            allowedOrganizations: allowedOrganizations,
                            organizationId: isOrgAdmin ? 'buenavista' : counselorDoc?.organizationId,
                            organizationName: isOrgAdmin ? 'BuenaVista Cooperativa Sociale' : counselorDoc?.organizationName,
                            canOrganizational: canOrganizational,
                            gdprConsent: counselorDoc?.gdprConsent || null
                        });
                        setLoading(false);
                        return;
                    }

                    // 2. Check if CLIENT (Exists in 'clients' collection)
                    // Lookup diretto per UID (O(1), no query)
                    let clientProfile = await DataManager.getClientByUid(firebaseUser.uid);

                    // Fallback: se creato preventivamente dal counselor con sola email prima del primo login
                    if (!clientProfile && userEmail) {
                        clientProfile = await DataManager.getClientByEmail(userEmail);
                        if (clientProfile && clientProfile.id !== firebaseUser.uid) {
                            // Collega l'ID al Firebase UID per accessi futuri O(1)
                            try {
                                await FirebaseService.changeClientId(clientProfile.id, firebaseUser.uid);
                                clientProfile.id = firebaseUser.uid;
                            } catch (linkErr) {
                                console.warn("Client UID linking warning:", linkErr);
                                clientProfile.id = firebaseUser.uid;
                            }
                        }
                    }

                    if (clientProfile) {
                        setUser({
                            ...clientProfile,
                            id: clientProfile.id || firebaseUser.uid,
                            role: 'client',
                            firebaseUid: firebaseUser.uid
                        });
                    } else {
                        // 3. UNAUTHORIZED / UNKNOWN
                        console.warn("Unauthorized user tried to login:", firebaseUser.email);
                        await AuthService.logout();
                        alert("Accesso non autorizzato. Contatta il tuo counselor per registrarti.");
                        setUser(null);
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                    await AuthService.logout();
                    setUser(null);
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleLogout = async () => {
        await AuthService.logout();
        setUser(null);
    };

    const handleConsentAccept = async (consentData) => {
        try {
            if (user.role === 'client') {
                await DataManager.updateClient(user.id, { gdprConsent: consentData });
            } else {
                await FirebaseService.updateCounselor(user.id, { gdprConsent: consentData });
            }
            setUser(prev => ({ ...prev, gdprConsent: consentData }));
        } catch (error) {
            console.error("Error saving GDPR consent:", error);
            throw error;
        }
    };

    if (currentPath === '/bioenergeticamente-te' || currentPath === '/bioenergeticamente-te/') {
        return <PublicSomaticExperiment onBack={() => navigate('/')} />;
    }

    if (currentPath === '/insight' || currentPath === '/insight/') {
        return <PublicInsightForm onBack={() => navigate('/')} navigate={navigate} />;
    }

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>;
    }

    if (!user) {
        if (showProspect) {
            return <ProspectLanding onBack={() => setShowProspect(false)} navigate={navigate} />;
        }
        return <LoginScreen onShowProspect={() => setShowProspect(true)} />;
    }

    // 🔒 GDPR Consent Gate: Mandatory consent check (Art. 6, 7 & 9 GDPR)
    if (!user.gdprConsent?.accepted) {
        return (
            <GDPRConsentModal 
                user={user} 
                onAccept={handleConsentAccept} 
                onReject={handleLogout} 
            />
        );
    }

    // Show CounselorDashboard if user is a counselor or admin
    if (user.role === 'counselor' || user.role === 'admin' || user.role === 'org_admin' || user.role === 'superadmin' || user.isAdmin || user.isOrgAdmin) {
        return <CounselorDashboard user={user} onLogout={handleLogout} />;
    }

    // Client Routing: Check onboarding status
    // If hasCompletedOnboarding is explicitly true, go to Dashboard.
    // Otherwise, show Questionnaire.
    if (user.role === 'client' && !user.hasCompletedOnboarding) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-brand-50 to-pink-50 p-6 flex flex-col items-center justify-center">
                <div className="bg-white rounded-3xl shadow-lg p-8 mb-6 max-w-4xl w-full">
                    <div className="flex items-start gap-4">
                        <span className="text-5xl">📋</span>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                Benvenuto, {user.name}!
                            </h2>
                            <p className="text-gray-600 mb-4">
                                Per iniziare il tuo percorso personalizzato, ti chiediamo di rispondere a qualche domanda.
                            </p>
                        </div>
                    </div>
                </div>
                <BaselineQuestionnaire
                    clientId={user.id}
                    onComplete={(baseline) => {
                        // Update local user state to reflect completion
                        setUser({ ...user, hasCompletedOnboarding: true });
                    }}
                />
                <button onClick={handleLogout} className="mt-8 text-gray-500 hover:text-gray-800 underline">
                    Esci e continua dopo
                </button>
            </div>
        );
    }

    // Show ClientView for existing clients (who have completed onboarding)
    return <ClientView user={user} onLogout={handleLogout} />;
}

export default App;